import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { createEditor, Transforms, Editor, Range, Path } from 'slate';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import { withHistory } from 'slate-history';
import { Music } from 'lucide-react';
import {
  buildMentionNode,
  prepareMentionAssets,
  getAssetsSignature,
  parseLineToChildren,
  withAutoMentions,
  forceConvertMentions,
} from './slateMentionUtils';
import MentionDropdown from '../node-parts/MentionDropdown';
import { getSlateCommandState } from '../utils/slateCommandUtils';

/**
 * Slate.js 富文本编辑器（分镜大师专用版）
 * 支持：
 * 1. @「素材名」气泡（和 SlatePromptEditor 一样）
 * 2. 分镜标题高亮（第X镜、时间段、Scene X）
 */

// ═══════════════════════════════════════════════════════════════
// 分镜标题正则（和 storyboardParser.js 保持一致）
// ═══════════════════════════════════════════════════════════════
const CN_NUMS = '零一二三四五六七八九十百千两';
const NUM = `[${CN_NUMS}\\d]+`;
const SEP = `[\\-~～到至]`;
const TIME_COLON = `\\d{1,2}:\\d{2}\\s*${SEP}\\s*\\d{1,2}:\\d{2}`;
const TIME_SEC = `\\d+(?:\\.\\d+)?\\s*[秒s]?\\s*${SEP}\\s*\\d+(?:\\.\\d+)?\\s*[秒s]`;
const TIME = `(?:${TIME_COLON}|${TIME_SEC})`;
const SHOT = `(?:第\\s*${NUM}\\s*[镜幕场]|${NUM}\\s*[镜幕场]|镜\\s*${NUM}|镜头\\s*(?:第\\s*)?${NUM}|[Ss]cene\\s+\\d+|[Ss]hot\\s+\\d+)`;
const LINE_START_RE = new RegExp(`(${SHOT})|${TIME}`);

// 🔧 生成小缩略图 URL（OSS/MinIO 实时缩放 90px）
const getMiniThumbUrl = (thumbUrl) => {
  if (!thumbUrl) return null;
  // 如果已经是 mini_thumbnail_url 或包含缩放参数，直接返回
  if (thumbUrl.includes('mini_thumb') || thumbUrl.includes('x-oss-process') || thumbUrl.includes('image-process')) {
    return thumbUrl;
  }
  // OSS/MinIO 实时缩放到 90px
  const isOSS = thumbUrl.includes('aliyuncs.com');
  const isMinIO = thumbUrl.includes('/cineart-media/') || thumbUrl.includes('/huanshe-media/');
  if (isOSS) {
    return thumbUrl + (thumbUrl.includes('?') ? '&' : '?') + 'x-oss-process=image/resize,w_90';
  } else if (isMinIO) {
    return thumbUrl + (thumbUrl.includes('?') ? '&' : '?') + 'image-process=resize,w_90';
  }
  return thumbUrl;
};

// 🎨 Mention 节点渲染（灰色气泡，与其他节点一致）
const MentionElement = ({ attributes, children, element }) => {
  const isAudio = element.assetType === 'audio' ||
                  ['music', 'sfx', 'voice'].includes(element.category);
  const isDarkMode = document.documentElement.getAttribute('data-theme') !== 'light';

  return (
    <span
      {...attributes}
      contentEditable={false}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        // 尺寸全用 em：字号跟随正文（右侧框 10px / 全屏编辑器按预设字号），气泡同步缩放
        gap: '0.3em',
        padding: '0 0.4em 0 0.25em',
        borderRadius: '0.3em',
        // 配色与 SlatePromptEditor（其他节点）一致：灰色气泡
        background: 'rgba(60, 60, 70, 0.8)',
        border: '1px solid rgba(100, 100, 110, 0.3)',
        fontSize: 'inherit',
        fontWeight: 400,
        lineHeight: 1.25, // 比正文行高(1.5)略小，含边框也不会撑高所在行
        color: isDarkMode ? '#F8FAFC' : '#1F2937',
        verticalAlign: 'middle',
        userSelect: 'none',
        cursor: 'default',
      }}
    >
      {isAudio ? (
        <Music size="1.2em" color="#fb923c" strokeWidth={1.5} style={{ flexShrink: 0 }} />
      ) : element.thumbUrl ? (
        <img src={getMiniThumbUrl(element.thumbUrl)} alt="" style={{ width: '1.2em', height: '1.2em', borderRadius: '0.2em', objectFit: 'cover', flexShrink: 0 }} />
      ) : null}
      {element.title}
      {children}
    </span>
  );
};

// 🎬 分镜标题节点渲染（粉色高亮）
const StoryboardHeadingElement = ({ attributes, children, element }) => {
  return (
    <span
      {...attributes}
      contentEditable={false}
      style={{
        padding: '3px 8px',
        borderRadius: '4px',
        background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.15), rgba(168, 85, 247, 0.15))',
        border: '1px solid rgba(236, 72, 153, 0.3)',
        fontSize: '14px',
        fontWeight: 600,
        color: '#EC4899',
        verticalAlign: 'middle',
        userSelect: 'none',
        cursor: 'default',
        boxShadow: '0 1px 3px rgba(236, 72, 153, 0.1)',
      }}
    >
      {element.text}
      {children}
    </span>
  );
};

// 渲染元素
const Element = (props) => {
  const { attributes, children, element } = props;

  switch (element.type) {
    case 'mention':
      return <MentionElement {...props} />;
    case 'storyboard-heading':
      return <StoryboardHeadingElement {...props} />;
    default:
      return <p {...attributes}>{children}</p>;
  }
};

// 渲染叶子节点
const Leaf = ({ attributes, children }) => {
  return <span {...attributes}>{children}</span>;
};

// 自定义编辑器行为
const withMentionsAndHeadings = (editor) => {
  const { isInline, isVoid, insertData } = editor;

  editor.isInline = (element) => {
    return ['mention', 'storyboard-heading'].includes(element.type) ? true : isInline(element);
  };

  editor.isVoid = (element) => {
    return ['mention', 'storyboard-heading'].includes(element.type) ? true : isVoid(element);
  };

  // 粘贴的文本整段转换，不等光标移开
  editor.insertData = (data) => {
    insertData(data);
    forceConvertMentions(editor);
  };

  return withAutoMentions(editor);
};

/**
 * 将 Slate 值转换为纯文本
 * mention 节点 → @「标题」
 * storyboard-heading 节点 → 原文
 */
const slateValueToText = (value) => {
  return value
    .map((node) => {
      if (node.children) {
        return node.children
          .map((child) => {
            if (child.type === 'mention') {
              return `@「${child.title}」`;
            }
            if (child.type === 'storyboard-heading') {
              return child.text;
            }
            return child.text || '';
          })
          .join('');
      }
      return '';
    })
    .join('\n');
};

/**
 * 将纯文本解析为 Slate 富文本结构
 * 支持：
 * 1. @「标题」→ mention 气泡
 * 2. 分镜标题（第X镜、时间段）→ storyboard-heading 高亮
 */
export const parseTextToSlate = (text, assets = []) => {
  if (!text || typeof text !== 'string') {
    return [{ type: 'paragraph', children: [{ text: '' }] }];
  }

  const sortedAssets = prepareMentionAssets(assets);

  return text.split('\n').map((line) => {
    const children = [];

    // ① 先检测分镜标题（行首匹配）
    const headingMatch = line.match(LINE_START_RE);
    if (headingMatch) {
      const headingText = headingMatch[0];
      const headingEnd = headingMatch.index + headingText.length;

      // 添加前面的文本（标题前面的内容也可能有 @资产名）
      if (headingMatch.index > 0) {
        children.push(...parseLineToChildren(line.slice(0, headingMatch.index), sortedAssets)
          .filter(c => c.type || c.text));
      }

      // 添加分镜标题节点
      children.push({
        type: 'storyboard-heading',
        text: headingText,
        children: [{ text: '' }]
      });

      line = line.slice(headingEnd); // 剩余部分继续解析 mention
    }

    // ② 解析剩余部分的 @资产名 / @「资产名」
    if (line.length > 0) {
      children.push(...parseLineToChildren(line, sortedAssets));
    }

    // ③ 空行保护 + Slate 要求行内 void 节点两侧都有文本节点
    if (children.length === 0 || children[children.length - 1].type) {
      children.push({ text: '' });
    }
    if (children[0].type) {
      children.unshift({ text: '' });
    }

    return {
      type: 'paragraph',
      children
    };
  });
};

/**
 * 从外部值得到初始 Slate 结构：纯文本是权威，slateValue 只是气泡缓存。
 * 两者对不上（如分镜大师回流只更新 parseText）时按纯文本重新解析。
 */
const resolveInitialValue = (externalSlateValue, externalValue, assets) => {
  if (externalSlateValue) {
    try {
      const parsed = typeof externalSlateValue === 'string'
        ? JSON.parse(externalSlateValue)
        : externalSlateValue;
      if (Array.isArray(parsed) && parsed.length && slateValueToText(parsed) === (externalValue || '')) {
        return parsed;
      }
    } catch (e) {
      console.warn('[SlateStoryboardEditor] 解析 slateValue 失败:', e);
    }
  }
  return parseTextToSlate(externalValue || '', assets);
};

const SlateStoryboardEditor = ({
  value: externalValue = '',
  slateValue: externalSlateValue,
  assets = [], // 🆕 需要传入 assets 用于解析
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  placeholder,
  disabled,
  editorRef,
  style,
  className,
  forceUpdate = false, // 🆕 强制更新标志（全屏编辑器保存时）
  showMentionDropdown = true, // 🆕 是否显示 @ 下拉（分镜大师右侧输入框传 false，已有整个面板的下拉）
}) => {
  const editor = useMemo(
    () => withMentionsAndHeadings(withHistory(withReact(createEditor()))),
    []
  );

  const containerRef = useRef(null);

  // 素材按签名缓存：父组件每次渲染都会新建 allAssets 数组，签名不变就不重新匹配
  const assetsSignature = getAssetsSignature(prepareMentionAssets(assets));
  const sortedAssets = useMemo(() => prepareMentionAssets(assets), [assetsSignature]); // eslint-disable-line react-hooks/exhaustive-deps
  editor.mentionAssets = sortedAssets;

  // 初始化：slateValue 与纯文本一致就用 slateValue，否则解析 externalValue（含 @资产名 气泡）
  const [value, setValue] = useState(() => resolveInitialValue(externalSlateValue, externalValue, assets));

  // 🆕 @ 下拉状态（只支持 @，不支持 /）
  const [commandState, setCommandState] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null); // 🆕 记录下拉框位置（Portal 用）

  const isFocusedRef = useRef(false);
  const isSelectingAssetRef = useRef(false); // 🔧 正在选择资产的标志位，防止 blur 干扰
  const prevExternalValueRef = useRef(externalValue);
  const prevExternalSlateValueRef = useRef(externalSlateValue);

  // 📍 计算下拉框位置（相对 viewport）
  useEffect(() => {
    if (showMentionDropdown && commandState && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const position = {
        left: rect.left,
        top: rect.bottom - 280, // 下拉框高度 280px，贴在容器底部
        width: rect.width,
      };
      console.log('[Portal] 计算下拉框位置:', { rect, position, showMentionDropdown, commandState });
      setDropdownPosition(position);
    } else {
      console.log('[Portal] 隐藏下拉:', { showMentionDropdown, commandState, hasContainer: !!containerRef.current });
      setDropdownPosition(null);
    }
  }, [showMentionDropdown, commandState]);

  // 外部值变化时同步
  useEffect(() => {
    // 🆕 生成过程中（通过 disabled 判断）强制同步，无视聚焦状态
    const isGenerating = disabled; // disabled 在生成时为 true
    if (!forceUpdate && !isGenerating && isFocusedRef.current) return;

    // 自己 onChange 回流的值已在 handleChange 记过，不会重复替换
    // 🔧 但如果 forceUpdate 变化了（数字计数器递增），说明外部要求强制同步，跳过这个检查
    if (!forceUpdate && externalSlateValue === prevExternalSlateValueRef.current && externalValue === prevExternalValueRef.current) {
      return;
    }
    prevExternalSlateValueRef.current = externalSlateValue;
    prevExternalValueRef.current = externalValue;

    // 分镜大师回流只更新 parseText，slateValue 是旧的 → 对不上时按纯文本重新解析出气泡
    const newValue = resolveInitialValue(externalSlateValue, externalValue, sortedAssets);
    Editor.withoutNormalizing(editor, () => {
      for (let i = editor.children.length - 1; i >= 0; i--) {
        Transforms.removeNodes(editor, { at: [i] });
      }
      Transforms.insertNodes(editor, newValue, { at: [0] });
    });
    forceConvertMentions(editor); // 旧 slateValue 里残留的纯文本 @资产名 也转成气泡
    setValue(newValue);
  }, [externalValue, externalSlateValue, sortedAssets, editor, forceUpdate, disabled]); // 🆕 加入 disabled 依赖

  // 🆕 素材库异步加载晚于编辑器渲染：素材到位后补一次全量转换（编辑中不打扰）
  useEffect(() => {
    if (!isFocusedRef.current) forceConvertMentions(editor);
  }, [sortedAssets, editor]);

  // 🆕 生成过程中自动滚动到底部
  useEffect(() => {
    if (disabled && containerRef.current) {
      // 延迟执行，确保 DOM 已更新
      requestAnimationFrame(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
      });
    }
  }, [disabled, externalValue]); // 监听 disabled 和 externalValue 变化

  // 全局样式（根据主题动态设置 placeholder 颜色）
  useEffect(() => {
    const styleId = 'slate-storyboard-editor-fix';

    // 读取当前主题
    const updatePlaceholderColor = () => {
      let style = document.getElementById(styleId);
      if (!style) {
        style = document.createElement('style');
        style.id = styleId;
        document.head.appendChild(style);
      }

      // 🆕 使用 CSS 变量 var(--sb-cell-placeholder)，自适应亮/暗模式
      style.textContent = `
        [data-slate-editor] [data-slate-placeholder] {
          color: var(--sb-cell-placeholder, rgba(156, 163, 175, 0.85)) !important;
          position: absolute !important;
          top: 0 !important;
          pointer-events: none !important;
        }
        [data-slate-editor] *::selection {
          background: rgba(96, 165, 250, 0.3) !important;
        }
        [data-slate-editor] [data-slate-node],
        [data-slate-editor] [data-slate-leaf] {
          background: transparent !important;
        }
        [data-slate-editor] p {
          margin: 0 !important;
        }
      `;
    };

    // 初始设置
    updatePlaceholderColor();

    // 监听主题变化（通过 MutationObserver 监听 data-theme 属性变化）
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          updatePlaceholderColor();
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  const handleChange = useCallback((newValue) => {
    setValue(newValue);
    const text = slateValueToText(newValue);
    const slateJson = JSON.stringify(newValue);
    // 记下自己发出的值：父组件回传同样的值时不再整体替换
    prevExternalValueRef.current = text;
    prevExternalSlateValueRef.current = slateJson;
    onChange({
      target: {
        value: text,
        slateValue: slateJson
      }
    });

    // 🆕 检测 @ 触发下拉（只支持 @，不支持 /）
    const state = getSlateCommandState(editor, ['@']);
    setCommandState(state);
  }, [onChange, editor]);

  // 🆕 @ 下拉选中回调
  const handleMentionSelect = useCallback((asset) => {
    if (!commandState) return;

    isSelectingAssetRef.current = true; // 🔧 标记开始选择资产

    const { selection } = editor;
    if (!selection) return;

    const cursorPoint = Range.start(selection);
    const beforeRange = Editor.range(editor, Editor.start(editor, []), cursorPoint);
    const beforeText = Editor.string(editor, beforeRange);
    const atIndex = beforeText.lastIndexOf('@');
    if (atIndex === -1) return;

    const startPoint = Editor.point(editor, [0], { edge: 'start' });
    let current = 0;
    let deleteStart = null;

    for (const [node, path] of Editor.nodes(editor, { at: [] })) {
      if (!node.text) continue;
      if (current + node.text.length >= atIndex) {
        const offset = atIndex - current;
        deleteStart = { path, offset };
        break;
      }
      current += node.text.length;
      if (path.length === 1) current += 1; // 换行
    }

    if (!deleteStart) return;

    const queryLength = commandState.query.length;
    const deleteEnd = Editor.after(editor, deleteStart, { distance: 1 + queryLength });

    // 🔧 插入 mention 节点 + 自动添加空格
    Editor.withoutNormalizing(editor, () => {
      if (deleteEnd) {
        Transforms.delete(editor, { at: { anchor: deleteStart, focus: deleteEnd } });
      }
      const mentionNode = buildMentionNode(asset);
      Transforms.insertNodes(editor, mentionNode, { at: deleteStart });
    });

    setCommandState(null);

    // 🔧 选中素材后，插入空格并移动光标到空格后面
    // 使用 requestAnimationFrame 确保 DOM 更新完成后再操作
    requestAnimationFrame(() => {
      // 🔧 找到刚插入的 mention 节点的位置
      const [mentionEntry] = Editor.nodes(editor, {
        at: [],
        match: n => n.type === 'mention' && n.title === asset.title,
        mode: 'all',
        reverse: true, // 从后往前找，拿到最新插入的
      });

      if (mentionEntry) {
        const [, mentionPath] = mentionEntry;
        // 🔧 在 mention 节点后面插入空格
        const afterMentionPath = Path.next(mentionPath);
        Transforms.insertText(editor, ' ', { at: Editor.end(editor, mentionPath) });

        // 🔧 移动光标到空格后面
        const afterSpace = Editor.after(editor, mentionPath, { unit: 'offset' });
        if (afterSpace) {
          Transforms.select(editor, afterSpace);
        }
      }

      ReactEditor.focus(editor);
      // 🔧 手动设置焦点状态，确保编辑器认为自己已聚焦
      isFocusedRef.current = true;
      isSelectingAssetRef.current = false; // 🔧 标记选择完成
    });
  }, [editor, commandState]);

  const handleKeyDown = useCallback((event) => {
    if (onKeyDown) onKeyDown(event);
  }, [onKeyDown]);

  const handleFocus = useCallback((event) => {
    isFocusedRef.current = true;
    if (onFocus) onFocus(event);
  }, [onFocus]);

  const handleBlur = useCallback((event) => {
    // 🔧 如果正在选择资产，不触发 blur（避免焦点丢失和中文输入法失效）
    if (isSelectingAssetRef.current) {
      event.preventDefault();
      return;
    }

    isFocusedRef.current = false;
    forceConvertMentions(editor); // 光标停在 @资产名 末尾时打字阶段没转，失焦补转
    if (onBlur) onBlur(event);
  }, [onBlur, editor]);

  const renderElement = useCallback((props) => <Element {...props} />, []);
  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);

  // 插入 mention（从外部调用）
  const insertMention = useCallback((asset) => {
    if (!asset) return;

    const { selection } = editor;
    if (!selection) return;

    // 🔧 如果光标在 mention 节点内部，先移出来
    const [mentionEntry] = Editor.nodes(editor, {
      match: n => n.type === 'mention',
      at: selection
    });

    if (mentionEntry) {
      // 光标在 mention 内部，移动到该 mention 后面
      const [, mentionPath] = mentionEntry;
      const after = Editor.after(editor, mentionPath);
      if (after) {
        Transforms.select(editor, after);
      }
    }

    const [start] = Range.edges(editor.selection);
    const lineStart = Editor.before(editor, start, { unit: 'line' });
    const lineRange = lineStart ? Editor.range(editor, lineStart, start) : null;

    if (lineRange) {
      const lineText = Editor.string(editor, lineRange);
      const match = lineText.match(/@([^@\s]*)$/);

      if (match) {
        const matchLength = match[0].length;
        const deleteRange = {
          anchor: { ...start, offset: start.offset - matchLength },
          focus: start
        };
        Transforms.delete(editor, { at: deleteRange });
      }
    }

    // ⚠️ 气泡显示"迷你缩略图"（80px），不是标准缩略图（768px）或原图
    Transforms.insertNodes(editor, buildMentionNode(asset));
    Transforms.move(editor);
  }, [editor]);

  // 暴露方法给父组件
  useEffect(() => {
    if (editorRef) {
      editorRef.current = containerRef.current;
      editorRef.current.insertMention = insertMention;
      editorRef.current.focus = () => ReactEditor.focus(editor);
      editorRef.current.blur = () => ReactEditor.blur(editor);
      editorRef.current.editor = editor;
    }
  }, [editorRef, insertMention, editor]);

  return (
    <>
      <Slate editor={editor} initialValue={value} value={value} onChange={handleChange}>
        <div
          ref={containerRef}
          style={{
            ...style, // 🔧 先展开外部 style（可能有 flex, fontSize等）
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: 0, // 🔧 允许 flex 收缩
          }}
        >
          <Editable
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder={placeholder}
            readOnly={disabled}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              outline: 'none',
              background: 'transparent',
              color: 'inherit',
              fontSize: style?.fontSize || 'inherit',
              fontWeight: style?.fontWeight || 'inherit',
              cursor: 'text',
            }}
            className={className}
          />
        </div>
      </Slate>

      {/* 🆕 @ 下拉（Portal 到 body，完全独立渲染，不受容器布局影响） */}
      {showMentionDropdown && commandState && dropdownPosition && createPortal(
        <div
          data-mention-dropdown // 🔧 标记下拉框，用于 blur 判断
          style={{
            position: 'fixed',
            left: dropdownPosition.left,
            top: dropdownPosition.top,
            width: dropdownPosition.width,
            height: '280px',
            zIndex: 1000000, // 必须高于全屏弹窗的 999999
          }}
        >
          <MentionDropdown
            query={commandState.query}
            assets={sortedAssets}
            onSelect={handleMentionSelect}
            onClose={() => setCommandState(null)}
          />
        </div>,
        document.body
      )}
    </>
  );
};

export default SlateStoryboardEditor;
